/**
 * AuthLayout — Split-pane auth screen wrapper.
 *
 * Desktop (>=768px):  440px branded left panel (gradient + dot grid + feature pills)
 *                     + flex-1 right panel (centered form).
 * Mobile (<768px):    Brand panel on top (rounded bottom corners, responsive height)
 *                     + form card below.
 *
 * The mobile brand panel uses flex-1 with min/max-height so it expands to
 * fill available vertical space above the card on taller phones, preventing
 * large blank gaps — but doesn't grow excessively.
 *
 * @param children  — The form card content rendered in the right/bottom panel.
 * @param ownerMode — When true, renders the dark Owner Portal variant.
 */

import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { BrandLogo } from '../brand/BrandLogo';
import { TaglineRotator } from './TaglineRotator';
import { LanguageIconButton } from '../common/LanguageIconButton';
import { FeatureChip } from './FeatureChip'; // OCCASION-CARDS-1 Slice 2 — one chip, four call sites

interface AuthLayoutProps {
  children: ReactNode;
  ownerMode?: boolean;
  /** OLYMPIAD-P2D — override the left-panel rotating lines + chip (defaults keep every other page unchanged). */
  taglineMessages?: string[];
  chipLabel?: string;
}

// Owner 2FA is TOTP (authenticator), not WhatsApp — chip text corrected. The two other owner
// chips ('22 dashboard cards', 'System Health Monitor') were removed per LOGIN-UI-1.
// JARGON-1: shown on the public owner-login panel → plain wording, no "TOTP"/"Whitelist".
const OWNER_FEATURES = ['Two-factor login + IP allowlist'];

export function AuthLayout({ children, ownerMode = false, taglineMessages, chipLabel }: AuthLayoutProps) {
  const { t } = useTranslation();
  // Owner-mode copy stays English (the owner portal is never translated; language is forced to
  // English for the owner role). Only the student/parent-facing chip + footer are translated.
  const chip = chipLabel ?? t('common:brand.features_chip');
  /*
   * [OCCASION-CARDS-1 Slice 2] The chip claimed a NUMBER ("22 AI-Powered Features"), which is the kind
   * of copy that quietly becomes false — nobody recounts it when a feature ships or is dropped. The
   * replacement states the promise instead, and the supporting line says what that means for a parent
   * reading it on a phone. Both are single locale strings so the wording is a one-line swap.
   *
   * The supporting line is suppressed when a caller passes an explicit `chipLabel`: that caller chose
   * its own chip text, and a stock subtitle underneath it would read as a mismatched pair.
   */
  const chipSub = chipLabel ? '' : t('common:brand.features_chip_sub');
  const features = OWNER_FEATURES;
  const pillDotColor = ownerMode ? '#A5B4FC' : '#86EFAC';

  const gradientStyle = ownerMode
    ? 'linear-gradient(160deg, #0D0B1E 0%, #1A1828 40%, #3730A3 100%)'
    : 'linear-gradient(160deg, #4F46E5 0%, #6D28D9 50%, #7C3AED 100%)';

  return (
    <div
      className="min-h-dvh flex flex-col md:flex-row font-body"
      style={{ background: 'var(--bg)' }}
    >
      {/* ── MOBILE BRAND PANEL (< 768px) ── */}
      <div
        className="md:hidden relative overflow-hidden flex flex-col justify-between"
        style={{
          background: gradientStyle,
          padding: '28px 24px 24px',
          borderRadius: '0 0 24px 24px',
          flex: '1 1 0',
          minHeight: 290,
          maxHeight: 360,
        }}
      >
        {/* Dot grid */}
        <div
          className="absolute inset-0 z-0"
          style={{
            backgroundImage:
              'radial-gradient(rgba(255,255,255,0.07) 1px, transparent 1px)',
            backgroundSize: '22px 22px',
          }}
        />

        {/* Decorative circles */}
        <div
          className="absolute rounded-full z-0"
          style={{
            width: 240,
            height: 240,
            background: 'rgba(255,255,255,0.04)',
            top: -80,
            right: -60,
          }}
        />
        <div
          className="absolute rounded-full z-0"
          style={{
            width: 140,
            height: 140,
            background: 'rgba(255,255,255,0.04)',
            bottom: -30,
            left: -30,
          }}
        />

        {ownerMode ? (
          <>
            {/* Top content */}
            <div className="relative z-10">
              <div className="mb-3">
                <BrandLogo size="login" tone="white" />
              </div>
              <h2 className="font-display text-lg font-extrabold leading-tight tracking-[-0.03em] text-white mb-1.5">
                Platform control centre.
              </h2>
              <p className="text-[11px] font-medium text-white/55 leading-relaxed">
                Monitor all schools, billing, and system health.
              </p>
            </div>

            {/* Feature pills — horizontal wrap */}
            <div className="relative z-10 flex gap-2 flex-wrap mt-3">
              {features.map((feature) => (
                <FeatureChip key={feature} size="mobile" dotColor={pillDotColor}>{feature}</FeatureChip>
              ))}
            </div>
          </>
        ) : (
          <div className="login-ui-v2 relative z-10 flex flex-1 flex-col">
            {/* LANG-2 overlap fix: header ROW — logo LEFT, language pill RIGHT. A real flex row (not an
                absolute overlay), so the pill can never cover the wordmark at any width ≥360px. */}
            <div className="flex items-center justify-between gap-3">
              <BrandLogo size="sidebar" tone="white" />
              <div className="shrink-0"><LanguageIconButton variant="pill" tone="onDark" /></div>
            </div>
            {/* hero — tagline + chip, centered in the remaining space */}
            <div className="flex flex-1 flex-col items-center justify-center text-center" style={{ gap: 24 }}>
              <TaglineRotator size="mobile" messages={taglineMessages} />
              <div className="flex flex-col items-center" style={{ gap: 10 }}>
                <FeatureChip size="mobile">{chip}</FeatureChip>
                {chipSub ? (
                  <p className="max-w-[280px] text-[11.5px] font-medium leading-snug text-white/60">{chipSub}</p>
                ) : null}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ── DESKTOP LEFT PANEL (>= 768px) ── */}
      <div
        className="hidden md:flex w-[440px] shrink-0 relative overflow-hidden flex-col justify-between p-11"
        style={{ background: gradientStyle }}
      >
        <div
          className="absolute inset-0 z-0"
          style={{
            backgroundImage:
              'radial-gradient(rgba(255,255,255,0.07) 1px, transparent 1px)',
            backgroundSize: '22px 22px',
          }}
        />
        <div
          className="absolute w-[360px] h-[360px] rounded-full z-0"
          style={{
            background: 'rgba(255,255,255,0.04)',
            top: -120,
            right: -100,
          }}
        />
        <div
          className="absolute w-[220px] h-[220px] rounded-full z-0"
          style={{
            background: 'rgba(255,255,255,0.04)',
            bottom: 60,
            left: -60,
          }}
        />

        {ownerMode ? (
          <div className="relative z-10 flex flex-1 flex-col items-center justify-center text-center gap-6">
            <BrandLogo size="login" tone="white" />
            <h2 className="font-display text-[22px] font-bold leading-snug tracking-[-0.02em] text-white/95 max-w-[340px]">
              Platform control centre.
            </h2>
            <p className="text-sm font-medium text-white/60 leading-[1.7] max-w-[340px]">
              Monitor all schools, billing, and system health.
            </p>
            {features.map((feature) => (
              <FeatureChip key={feature} size="desktop" dotColor={pillDotColor}>{feature}</FeatureChip>
            ))}
          </div>
        ) : (
          <div className="login-ui-v2 relative z-10 flex flex-1 flex-col items-center text-center" style={{ paddingTop: '9vh' }}>
            <BrandLogo size="login" tone="white" />
            {/* generous negative space (~11vh) below the lockup before the headline */}
            <div style={{ flex: '0 0 11vh' }} />
            {/* explicit stack: rotator(card→dots) then chip 24px below the dots — no margins that can collide */}
            <div className="flex flex-col items-center" style={{ gap: 24 }}>
              <TaglineRotator size="desktop" messages={taglineMessages} />
              <div className="flex flex-col items-center" style={{ gap: 12 }}>
                <FeatureChip size="desktop">{chip}</FeatureChip>
                {chipSub ? (
                  <p className="max-w-[320px] text-[13px] font-medium leading-relaxed text-white/60">{chipSub}</p>
                ) : null}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ── FORM PANEL ── */}
      <div className="flex-1 flex flex-col px-5 py-8 md:px-6 md:py-10">
        {/* LOGIN-POLISH-2: DESKTOP language switcher — a labelled pill top-right of the form panel, so a
            first-time user can READ that language is changeable. Hidden on mobile (the switcher there lives
            on the purple banner — see the mobile brand panel). Owner portal stays English. */}
        {!ownerMode && (
          <div className="hidden md:flex justify-end shrink-0 -mt-2 mb-1">
            <LanguageIconButton variant="pill" />
          </div>
        )}
        <div className="flex-1 flex items-center justify-center">
          {children}
        </div>
        {/*
          [UI-POLISH-3] Two lines, not one. The single em-dashed string wrapped wherever the viewport
          happened to break it, so "Powered by UNOSONIC" could split mid-phrase on a narrow phone.
          Explicit lines put the break where it belongs and keep both centred.
        */}
        <div className="shrink-0 text-center mt-6">
          <p className="text-[11px] font-medium m-0" style={{ color: 'var(--t4)' }}>
            {t('common:brand.footer_line1')}
          </p>
          <p className="text-[11px] font-medium m-0 mt-0.5" style={{ color: 'var(--t4)' }}>
            {t('common:brand.footer_line2')}
          </p>
        </div>
      </div>
    </div>
  );
}

export default AuthLayout;